import React, {
  ComponentProps,
  RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, TouchableOpacity } from 'react-native';

import { Box, ShadowBox, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import Images from '@themes/images';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';

type PositionTuple = ['left' | 'right', 'top' | 'bottom'];
type Position = PositionTuple | 'top' | 'bottom' | 'left' | 'right';

export type ProjectSortStatus = 'active' | 'archived';

type Props = Omit<ComponentProps<typeof TouchableOpacity>, 'onPress'> & {
  title: string;
  selectedStatus: ProjectSortStatus;
  onSelectStatus: (status: ProjectSortStatus) => void;
  boxProps: Partial<{
    position: Position;
    offset: [number, number] | number;
  }>;
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
};

const ITEM_HEIGHT = 36;

const STATUS_ITEMS: { key: ProjectSortStatus; i18nKey: string }[] = [
  { key: 'active', i18nKey: 'active' },
  { key: 'archived', i18nKey: 'archived' },
];

const SortListRow: React.FC<{
  label: string;
  selected: boolean;
  onPress: () => void;
}> = ({ label, selected, onPress }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <TouchableOpacity
      accessibilityRole='button'
      accessibilityLabel={label}
      onPress={onPress}>
      <Box
        height={ITEM_HEIGHT}
        px='m'
        flexDirection='row'
        alignItems='center'
        backgroundColor={hovered ? 'grey01' : 'white'}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}>
        <Box marginRight='xs'>
          <Icon
            name={selected ? 'StateYes' : 'StateNo'}
            variant='m'
            color={selected ? 'greenSecondary' : 'grey03'}
          />
        </Box>
        <Text
          flex={1}
          numberOfLines={1}
          variant='webBodySecondary'
          color='textPrimary'>
          {label}
        </Text>
      </Box>
    </TouchableOpacity>
  );
};

export const PopupGlobalSearchProjectsSortButtonWeb: React.FC<Props> = ({
  title,
  selectedStatus,
  onSelectStatus,
  boxProps: { position, offset },
  visible: visibleProp,
  onVisibleChange,
}) => {
  const { t } = useTranslation('shared');

  const [visibleLocal, setVisibleLocal] = useState(false);
  const isControlled = visibleProp !== undefined;
  const visible = isControlled ? visibleProp : visibleLocal;
  const setVisible = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const next = typeof value === 'function' ? value(visible) : value;
      if (isControlled) {
        onVisibleChange?.(next);
      } else {
        setVisibleLocal(next);
      }
    },
    [isControlled, visible, onVisibleChange]
  );

  const [mouseOver, setMouseOver] = useState(false);
  const popupRef = useRef<HTMLDivElement>();

  const isArchivedSelected = selectedStatus === 'archived';
  const badgeNumber = isArchivedSelected ? 1 : 0;

  const isEventPointInArea = (
    areaRef: RefObject<HTMLDivElement>,
    event: MouseEvent
  ) => {
    const rect = areaRef.current?.getBoundingClientRect();
    return (
      rect &&
      event.x > rect.x &&
      event.x < rect.x + rect.width &&
      event.y > rect.y &&
      event.y < rect.y + rect.height
    );
  };

  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (!isEventPointInArea(popupRef, event)) {
      setVisible(false);
    }
  }, []);

  const ref = useOutsideClick<HTMLDivElement>(handleOutsideClick);

  const [layout, setLayout] = useState<
    { width: number; height: number } | undefined
  >();

  const adjustBounds = (
    popupPosition: Props['boxProps']['position'] = 'bottom',
    popupOffset: Props['boxProps']['offset'] = 0,
    client: DOMRect,
    popupLayout: { width: number; height: number }
  ) => {
    const [offsetLeftRight, offsetTopBottom] =
      typeof popupOffset === 'number'
        ? [popupOffset, popupOffset]
        : popupOffset;

    const normalizedPosition = (typeof popupPosition !== 'string' &&
      popupPosition) ||
      ((popupPosition === 'left' || popupPosition === 'right') && [
        popupPosition,
        'bottom',
      ]) ||
      ((popupPosition === 'top' || popupPosition === 'bottom') && [
        'left',
        popupPosition,
      ]) || ['left', 'bottom'];

    const [leftRight, topBottom] = normalizedPosition;

    return {
      top:
        topBottom === 'top'
          ? client.top - popupLayout.height - offsetTopBottom
          : client.top + client.height + offsetTopBottom,
      left:
        leftRight === 'left'
          ? client.left + client.width - popupLayout.width - offsetLeftRight
          : client.left + offsetLeftRight,
    };
  };

  const adjustedBounds =
    ref.current &&
    layout &&
    adjustBounds(position, offset, ref.current.getBoundingClientRect(), layout);

  const backgroundColor =
    mouseOver || badgeNumber > 0 || visible ? 'grey01' : 'white';

  const popupContent = (
    <ShadowBox
      width={380}
      borderWidth={1}
      borderColor='grey02'
      borderRadius='xs'
      backgroundColor='white'
      variant='base'>
      <Box>
        <Box height={ITEM_HEIGHT} px='m' justifyContent='center'>
          <Text variant='labelEmphasized' color='textSecondary'>
            {t('status')}
          </Text>
        </Box>
        {STATUS_ITEMS.map((item) => (
          <SortListRow
            key={item.key}
            label={t(item.i18nKey)}
            selected={selectedStatus === item.key}
            onPress={() => onSelectStatus(item.key)}
          />
        ))}
        <Box
          px='m'
          height={ITEM_HEIGHT}
          borderTopWidth={1}
          borderTopColor='grey02'
          alignItems='center'
          justifyContent='center'>
          <TouchableOpacity
            disabled={!isArchivedSelected}
            accessibilityRole='button'
            accessibilityLabel={t('clearAll')}
            onPress={() => onSelectStatus('active')}>
            <Text
              variant='webBodySecondary'
              color={!isArchivedSelected ? 'grey04' : 'textPrimary'}>
              {t('clearAll')}
            </Text>
          </TouchableOpacity>
        </Box>
      </Box>
    </ShadowBox>
  );

  return (
    <Box ref={ref}>
      <TouchableOpacity
        accessibilityLabel={title}
        onPress={() => setVisible((current) => !current)}>
        <Box
          height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
          flexDirection='row'
          px='m'
          py='xs'
          borderRadius='xs'
          backgroundColor={backgroundColor}
          alignItems='center'
          justifyContent='center'
          onMouseEnter={() => setMouseOver(true)}
          onMouseLeave={() => setMouseOver(false)}>
          <Images.Sort3 color={theme.colors.black} />
          <Text variant='webSmall' color='black' ml='xs'>
            {title}
          </Text>
          {badgeNumber > 0 && (
            <TouchableOpacity
              accessibilityRole='button'
              accessibilityLabel={t('clearAll')}
              onPress={() => onSelectStatus('active')}>
              <Box
                width={16}
                height={16}
                marginLeft='xs'
                alignItems='center'
                justifyContent='center'>
                <Icon width={10} height={10} name='Close' color='black' />
              </Box>
            </TouchableOpacity>
          )}
        </Box>
      </TouchableOpacity>

      {!layout && (
        <Box
          pointerEvents='none'
          style={{ position: 'absolute', left: -9999, opacity: 0 }}
          onLayout={({ nativeEvent: { layout: nextLayout } }) => {
            setLayout((current) => current ?? nextLayout);
          }}>
          {popupContent}
        </Box>
      )}

      <Modal
        visible={visible && !!layout}
        transparent
        onRequestClose={() => setVisible(false)}
        onDismiss={() => setVisible(false)}>
        <Box ref={popupRef} style={adjustedBounds} width={layout?.width}>
          {popupContent}
        </Box>
      </Modal>
    </Box>
  );
};
